/**
 * `DEV-91.4` (`АУД-Н264`): отказ по счётчику — у ячейки **той** станции,
 * которую назвал сервер.
 *
 * Back называет станцию адресом контроллера: `станция 10: адрес 5 уже занят…`.
 * Прежде фронт брал текст тем же разбором, что и отказ по месту, и ставил его
 * всем правленым строкам — человек не понимал, к какой станции относится
 * причина. Здесь из текста достаётся названная станция, а причина остаётся
 * дословной.
 */
export const METER_CELL_FALLBACK =
  "изменение счётчика не принято — причина не названа сервером";

const METER_FIELDS = [
  "meter_address",
  "meter_number",
  "Meters.meter_address",
  "Meters.meter_number",
];

export interface MeterCellProblem {
  /** Адрес контроллера из текста сервера; `null` — сервер станцию не назвал. */
  station: string | null;
  message: string;
}

interface MeterCellErrorSource {
  response?: {
    status?: number;
    data?: {
      error?: string;
      message?: string;
      errors?: Array<{ field?: string; message?: string }>;
    };
  };
  message?: string;
}

/** Станция из текста отказа: `станция 10: …` → `10`. */
export function stationOfMeterMessage(message: string): string | null {
  const match = /^станция\s+([^:]+):/i.exec(String(message || "").trim());

  return match ? match[1].trim() : null;
}

/** Причина отказа по счётчику и станция из ответа сервера. */
export function meterCellError(error: any): MeterCellProblem | null {
  if (!error) return null;

  const source = error as MeterCellErrorSource;
  const data = source.response?.data;
  const status = source.response?.status;

  const fieldError = Array.isArray(data?.errors)
    ? data.errors.find((item) => METER_FIELDS.includes(String(item?.field)))
    : undefined;

  const message =
    fieldError?.message || data?.error || data?.message || null;

  if (message) {
    return { station: stationOfMeterMessage(message), message };
  }

  if (!status && source.message) {
    return {
      station: null,
      message: `изменение счётчика не принято: ${source.message}`,
    };
  }

  return { station: null, message: METER_CELL_FALLBACK };
}

/**
 * `S.2.1` (`АУД-Н265`): текст отказа формы «Замена счётчика».
 *
 * Заряд различается по коду `errors[0].code`, а не по тексту: иначе смена
 * формулировки на сервере ломала бы свою фразу на экране.
 */
export function meterReplaceErrorMessage(
  error: any,
  stationNumber: number | string,
): string {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const code = data?.errors?.[0]?.code;

  if (status === 409 && code === "station_charging") {
    return `Станция ${stationNumber} заряжает — замена счётчика после окончания заряда`;
  }

  return (
    data?.message || data?.error || error?.message || "Замена не принята"
  );
}
